import classNames from "classnames";
import { PieChart } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "components/common/buttons/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/common/tooltips/Tooltip";
import { BenchmarkComparisonSheet } from "./BenchmarkComparisonSheet";
import { BenchmarkDetailsSheet } from "./BenchmarkDetailsSheet";
import type { BenchmarkVerdictProps } from "./BenchmarkVerdict.types";

export const BenchmarkVerdict = ({
  comparison,
  source,
  onSourceChange,
  peerSampleSize,
  peerBenchmarkAvailable = false,
  meta,
  benchmarkSet,
  classificationAssessment,
}: BenchmarkVerdictProps) => {
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const surfaceOpen = comparisonOpen || detailsOpen;
  const wasSurfaceOpenRef = useRef(false);

  // Neither surface uses a Radix trigger, so Radix cannot restore focus on close. Return it to the
  // pie button once the last open surface closes, not while moving from comparison to details.
  useEffect(() => {
    if (wasSurfaceOpenRef.current && !surfaceOpen) {
      triggerRef.current?.focus();
    }
    wasSurfaceOpenRef.current = surfaceOpen;
  }, [surfaceOpen]);

  const isTypical = comparison.overallTypical;

  const tooltipLabel = "Compare to benchmarks";

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={triggerRef}
            variant="ghostSecondary"
            size="sm"
            aria-label={tooltipLabel}
            title={tooltipLabel}
            onClick={() => setComparisonOpen(true)}
            className="relative"
          >
            <PieChart className="h-4 w-4" aria-hidden />
            {!isTypical ? (
              <span
                className={classNames(
                  "absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-muted ring-2 ring-background"
                )}
                aria-hidden
              />
            ) : null}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{tooltipLabel}</TooltipContent>
      </Tooltip>

      <BenchmarkComparisonSheet
        open={comparisonOpen}
        onOpenChange={setComparisonOpen}
        comparison={comparison}
        source={source}
        onSourceChange={onSourceChange}
        peerSampleSize={peerSampleSize}
        peerBenchmarkAvailable={peerBenchmarkAvailable}
        classificationAssessment={classificationAssessment}
        onOpenDetails={() => {
          setComparisonOpen(false);
          setDetailsOpen(true);
        }}
      />

      <BenchmarkDetailsSheet
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        meta={meta}
        productionType={comparison.productionType}
        source={source}
        benchmarkSet={benchmarkSet}
      />
    </>
  );
};
